import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const selectCls = 'w-full h-10 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary';

export const NewRequestModal = ({ open, onOpenChange, onCreated }) => {
  const { user } = useAuth();
  const [allNeeds, setAllNeeds] = useState([]);
  const [regions, setRegions] = useState([]);
  const [surchargeDefault, setSurchargeDefault] = useState(0);
  const [loading, setLoading] = useState(false);

  const [category, setCategory] = useState('');
  const [selectedNeeds, setSelectedNeeds] = useState([]);
  const [suggestedPros, setSuggestedPros] = useState([]);
  const [excludedIds, setExcludedIds] = useState([]);
  const [loadingSugg, setLoadingSugg] = useState(false);
  const [form, setForm] = useState({
    region: '', province: '', comune: '', patient_name: '', family_member_name: '',
    contact_phone: '', budget: '', budget_type: 'from', description: '', health_conditions: '',
    is_urgent: false, privacy_consent: false,
  });
  const [provinces, setProvinces] = useState([]);

  useEffect(() => {
    if (!form.region) { setProvinces([]); return; }
    api.get(`/api/zones?region=${encodeURIComponent(form.region)}`)
      .then((r) => setProvinces(r.data || []))
      .catch(() => setProvinces([]));
  }, [form.region]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [n, r, cfg] = await Promise.all([
          api.get('/api/needs'), api.get('/api/regions'), api.get('/api/platform-config'),
        ]);
        setAllNeeds(n.data); setRegions(r.data);
        setSurchargeDefault(cfg.data.urgent_surcharge_default_eur || 0);
      } catch { /* ignore */ }
    })();
  }, [open]);

  const categories = useMemo(() => {
    const map = {};
    allNeeds.forEach((n) => {
      const key = n.context_code || 'altro';
      const label = n.category || n.context_label || 'Altro';
      if (!map[key]) map[key] = { code: key, label, items: [] };
      map[key].items.push(n);
    });
    return Object.values(map).sort((a, b) => a.label.localeCompare(b.label));
  }, [allNeeds]);

  const currentCategory = categories.find((c) => c.code === category);

  const uniqueProfessions = useMemo(() => {
    const codes = new Set();
    selectedNeeds.map((id) => allNeeds.find((n) => n.need_id === id)).filter(Boolean)
      .forEach((n) => (n.profession_codes || []).forEach((c) => codes.add(c)));
    return [...codes];
  }, [selectedNeeds, allNeeds]);

  const toggleNeed = (id) => setSelectedNeeds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  // Preview professionals who will be notified
  useEffect(() => {
    if (!open || !category || !form.region) { setSuggestedPros([]); return; }
    let active = true;
    setLoadingSugg(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.post('/api/requests/suggest-professionals', {
          profession_codes: uniqueProfessions,
          need_ids: selectedNeeds,
          region: form.region || null,
          comune: form.comune || null,
          province: form.province || null,
          budget: form.budget ? parseFloat(form.budget) : null,
          limit: 5,
        });
        if (active) setSuggestedPros(r.data.professionals || []);
      } catch { if (active) setSuggestedPros([]); }
      finally { if (active) { setLoadingSugg(false); setExcludedIds([]); } }
    }, 400);
    return () => { active = false; clearTimeout(t); };
  }, [open, category, selectedNeeds, uniqueProfessions, form.region, form.comune]);

  const reset = useCallback(() => {
    setCategory(''); setSelectedNeeds([]); setSuggestedPros([]); setExcludedIds([]);
    setForm({ region: '', province: '', comune: '', patient_name: '', family_member_name: '', contact_phone: '', budget: '', budget_type: 'from', description: '', health_conditions: '', is_urgent: false, privacy_consent: false });
  }, []);

  const toggleExclude = (id) => setExcludedIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const includedPros = suggestedPros.filter((p) => !excludedIds.includes(p.professional_id));

  const submit = async () => {
    if (!category) return toast.error('Scegli una prestazione');
    if (!form.region) return toast.error('Scegli una regione');
    if (!form.province) return toast.error('Scegli la provincia');
    if (!form.comune) return toast.error('Indica il comune');
    if (!form.patient_name || !form.contact_phone) return toast.error('Nome paziente e telefono sono obbligatori');
    if (!form.privacy_consent) return toast.error("Devi accettare l'informativa privacy");
    // If the client excluded some suggested pros, notify only the remaining ones explicitly
    let selectedIds = [];
    if (suggestedPros.length > 0 && excludedIds.length > 0) {
      if (includedPros.length === 0) return toast.error('Hai escluso tutti i professionisti: includine almeno uno o rimuovi le esclusioni');
      selectedIds = includedPros.map((p) => p.professional_id);
    }
    setLoading(true);
    try {
      const payload = {
        region: form.region, province: form.province, comune: form.comune,
        urgency: form.is_urgent ? 'high' : 'medium',
        is_urgent: form.is_urgent,
        urgent_surcharge_eur: form.is_urgent ? surchargeDefault : 0,
        budget: form.budget ? parseFloat(form.budget) : null,
        budget_type: form.budget_type,
        patient_name: form.patient_name, family_member_name: form.family_member_name,
        contact_phone: form.contact_phone, description: form.description,
        health_conditions: form.health_conditions,
        privacy_consent: form.privacy_consent,
        needs: selectedNeeds, need_category: category,
        profession_codes: uniqueProfessions, selected_professional_ids: selectedIds,
      };
      const res = await api.post('/api/requests', payload);
      toast.success('Richiesta inviata con successo!');
      reset();
      onOpenChange(false);
      onCreated?.(res.data?.request || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'invio");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="new-request-modal">
        <DialogHeader>
          <DialogTitle>Nuova richiesta di prestazione</DialogTitle>
          <DialogDescription>Compila il modulo: riceverai proposte dai professionisti giusti.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Prestazione *</Label>
            <select
              className={selectCls}
              value={category}
              onChange={(e) => { setCategory(e.target.value); setSelectedNeeds([]); }}
              data-testid="nr-category-select"
            >
              <option value="">Scegli una prestazione…</option>
              {categories.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>

          {currentCategory && currentCategory.items.length > 0 && (
            <div className="space-y-2">
              <Label>Bisogni specifici (opzionale)</Label>
              <div className="grid sm:grid-cols-2 gap-2 max-h-40 overflow-auto p-1" data-testid="nr-needs-list">
                {currentCategory.items.map((n) => (
                  <label key={n.need_id} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`nr-need-${n.need_id}`}>
                    <Checkbox checked={selectedNeeds.includes(n.need_id)} onCheckedChange={() => toggleNeed(n.need_id)} className="mt-0.5" />
                    <span>{n.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Regione *</Label>
              <select className={selectCls} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="nr-region-select">
                <option value="">Scegli regione…</option>
                {regions.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Provincia *</Label>
              <select
                className={selectCls}
                value={form.province}
                onChange={(e) => setForm({ ...form, province: e.target.value })}
                disabled={!form.region}
                data-testid="nr-province-select"
              >
                <option value="">Scegli provincia…</option>
                {provinces.map((z) => <option key={z.zone_id} value={z.province}>{z.province}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Comune *</Label>
              <Input value={form.comune} onChange={(e) => setForm({ ...form, comune: e.target.value })} placeholder="Es. Milano" data-testid="nr-comune-input" />
            </div>
            <div className="space-y-2">
              <Label>Nome paziente *</Label>
              <Input value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} data-testid="nr-patient-input" />
            </div>
            <div className="space-y-2">
              <Label>Telefono contatto *</Label>
              <Input type="tel" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="nr-phone-input" />
            </div>
            <div className="space-y-2">
              <Label>Email contatto</Label>
              <Input type="email" value={user?.email || ''} disabled data-testid="nr-email-input" />
              <p className="text-xs text-foreground/60">Email del tuo account: le comunicazioni arriveranno qui.</p>
            </div>
            <div className="space-y-2">
              <Label>Budget indicativo</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground/70 shrink-0">A partire da</span>
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground/60">€</span>
                  <Input type="number" min="0" className="pl-7" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} placeholder="es. 25" data-testid="nr-budget-input" />
                </div>
              </div>
              <p className="text-xs text-foreground/60">Facoltativo: vedrai prima i professionisti più vicini al tuo budget.</p>
            </div>
            <div className="space-y-2">
              <Label>Urgenza</Label>
              <select className={selectCls} value={form.is_urgent ? 'urgent' : 'normal'} onChange={(e) => setForm({ ...form, is_urgent: e.target.value === 'urgent' })} data-testid="nr-urgency-select">
                <option value="normal">Normale</option>
                <option value="urgent">Urgente (priorità + notifica immediata)</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descrizione</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Contesto, condizioni del paziente, aspettative…" data-testid="nr-description-input" />
          </div>

          <div className="space-y-2">
            <Label>Patologie o condizioni particolari <span className="text-foreground/50 text-xs">(opzionale)</span></Label>
            <Textarea
              rows={2} value={form.health_conditions}
              onChange={(e) => setForm({ ...form, health_conditions: e.target.value })}
              placeholder="Es. diabete, Alzheimer iniziale, allettamento, terapie in corso…"
              data-testid="nr-health-conditions-input"
            />
          </div>

          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <Checkbox checked={form.privacy_consent} onCheckedChange={(v) => setForm({ ...form, privacy_consent: !!v })} className="mt-0.5" data-testid="nr-privacy-checkbox" />
            <span>Accetto l'informativa privacy e il trattamento dei dati per gestire la richiesta. *</span>
          </label>

          {category && form.region && (
            <div className="rounded-xl border border-[#E3F2FD] bg-[#F5FAFF] p-3" data-testid="nr-suggested-pros">
              <p className="text-xs font-medium text-foreground/70 mb-2">
                {loadingSugg
                  ? 'Ricerca professionisti…'
                  : suggestedPros.length > 0
                    ? `${includedPros.length} professionist${includedPros.length === 1 ? 'a verrà notificato' : 'i verranno notificati'} al momento dell'invio`
                    : 'Nessun professionista corrispondente in zona: la richiesta sarà gestita dal team VivaLink.'}
              </p>
              {suggestedPros.length > 0 && (
                <div className="space-y-1.5">
                  {suggestedPros.map((p) => {
                    const excluded = excludedIds.includes(p.professional_id);
                    return (
                      <div key={p.professional_id} className={`flex items-center justify-between gap-2 text-sm bg-white rounded-lg border px-3 py-2 ${excluded ? 'border-dashed border-slate-200 opacity-55' : 'border-[#E3F2FD]'}`} data-testid={`nr-sugg-${p.professional_id}`}>
                        <button
                          type="button"
                          onClick={() => window.open(`/professional/${p.professional_id}`, '_blank', 'noopener')}
                          className="min-w-0 text-left group"
                          data-testid={`nr-sugg-open-${p.professional_id}`}
                        >
                          <p className={`font-medium truncate group-hover:text-primary group-hover:underline ${excluded ? 'line-through' : ''}`}>
                            {p.name}
                            {p.is_premium && <Badge className="ml-1 bg-accent text-white text-[10px]">PREMIUM</Badge>}
                          </p>
                          <p className="text-xs text-foreground/60 truncate">{p.location || p.region || '—'} · {p.hourly_rate}€/ora · ★ {p.rating ?? 0}</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleExclude(p.professional_id)}
                          className={`shrink-0 text-xs font-medium rounded-full px-2.5 py-1 border transition-colors ${excluded ? 'text-primary border-primary/40 hover:bg-primary/5' : 'text-red-600 border-red-200 hover:bg-red-50'}`}
                          data-testid={`nr-sugg-toggle-${p.professional_id}`}
                        >
                          {excluded ? 'Includi' : 'Escludi'}
                        </button>
                      </div>
                    );
                  })}
                  <p className="text-[11px] text-foreground/50 pt-0.5">Tocca un nome per aprire il profilo · usa "Escludi" per non contattarlo.</p>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} data-testid="nr-cancel-btn">Annulla</Button>
          <Button onClick={submit} disabled={loading} data-testid="nr-submit-btn">
            {loading ? 'Invio…' : 'Invia richiesta'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewRequestModal;
